// ============================================================================
// React Hooks - Contacts
// Easy-to-use hooks for contact management
// ============================================================================

import { useState, useEffect, useCallback } from 'react'
import {
  contactsApi,
  type CreateContactInput,
  type UpdateContactInput,
  type ContactFilters
} from '../api/contacts.api'
import type { ContactRecord, InteractionRecord } from '../types'

// ============================================================================
// useContacts Hook
// ============================================================================

export interface UseContactsOptions {
  filters?: ContactFilters
  autoRefresh?: boolean
  refreshInterval?: number
}

export interface UseContactsReturn {
  contacts: ContactRecord[]
  isLoading: boolean
  error: string | null
  create: (input: CreateContactInput) => Promise<ContactRecord>
  update: (id: string, updates: UpdateContactInput) => Promise<ContactRecord>
  remove: (id: string) => Promise<void>
  addInteraction: (contactId: string, interaction: Omit<InteractionRecord, 'id'>) => Promise<void>
  removeInteraction: (contactId: string, interactionId: string) => Promise<void>
  linkApplication: (contactId: string, applicationId: string) => Promise<void>
  unlinkApplication: (contactId: string, applicationId: string) => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
}

export function useContacts(options: UseContactsOptions = {}): UseContactsReturn {
  const [contacts, setContacts] = useState<ContactRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadContacts = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = options.filters
        ? await contactsApi.getFiltered(options.filters)
        : await contactsApi.getAll()
      setContacts(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts')
    } finally {
      setIsLoading(false)
    }
  }, [options.filters])

  useEffect(() => {
    loadContacts()
  }, [loadContacts])

  useEffect(() => {
    if (!options.autoRefresh || !options.refreshInterval) return
    const interval = setInterval(loadContacts, options.refreshInterval)
    return () => clearInterval(interval)
  }, [loadContacts, options.autoRefresh, options.refreshInterval])

  const create = useCallback(async (input: CreateContactInput) => {
    const contact = await contactsApi.create(input)
    setContacts(prev => [contact, ...prev])
    return contact
  }, [])

  const update = useCallback(async (id: string, updates: UpdateContactInput) => {
    const contact = await contactsApi.update(id, updates)
    setContacts(prev => prev.map(c => c.id === id ? contact : c))
    return contact
  }, [])

  const remove = useCallback(async (id: string) => {
    await contactsApi.delete(id)
    setContacts(prev => prev.filter(c => c.id !== id))
  }, [])

  const addInteraction = useCallback(async (contactId: string, interaction: Omit<InteractionRecord, 'id'>) => {
    await contactsApi.addInteraction(contactId, interaction)
    setContacts(prev => prev.map(c => {
      if (c.id !== contactId) return c
      return { ...c, interactions: [...c.interactions, { ...interaction, id: crypto.randomUUID() }], updatedAt: new Date() }
    }))
  }, [])

  const removeInteraction = useCallback(async (contactId: string, interactionId: string) => {
    await contactsApi.removeInteraction(contactId, interactionId)
    setContacts(prev => prev.map(c => {
      if (c.id !== contactId) return c
      return { ...c, interactions: c.interactions.filter(i => i.id !== interactionId), updatedAt: new Date() }
    }))
  }, [])

  const linkApplication = useCallback(async (contactId: string, applicationId: string) => {
    await contactsApi.linkApplication(contactId, applicationId)
    setContacts(prev => prev.map(c => {
      if (c.id !== contactId) return c
      if (c.linkedApplicationIds.includes(applicationId)) return c
      return { ...c, linkedApplicationIds: [...c.linkedApplicationIds, applicationId], updatedAt: new Date() }
    }))
  }, [])

  const unlinkApplication = useCallback(async (contactId: string, applicationId: string) => {
    await contactsApi.unlinkApplication(contactId, applicationId)
    setContacts(prev => prev.map(c => {
      if (c.id !== contactId) return c
      return { ...c, linkedApplicationIds: c.linkedApplicationIds.filter(id => id !== applicationId), updatedAt: new Date() }
    }))
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return {
    contacts,
    isLoading,
    error,
    create,
    update,
    remove,
    addInteraction,
    removeInteraction,
    linkApplication,
    unlinkApplication,
    refresh: loadContacts,
    clearError
  }
}
